import type { NormalizedAddress } from '@components/AddressCapture/AddressCapture.types';

// ---------------------------------------------------------------------------
// Enums (string literal unions)
// ---------------------------------------------------------------------------

export type QualificationStatus = 'PLANNED' | 'CONSTRUCTION' | 'SERVICEABLE' | 'ACTIVE' | 'RETIRED' | 'NON_BOOKABLE';

export type AddressType = 'RESIDENTIAL' | 'BUSINESS';

export type CartState = 'STARTED' | 'CART_IN_PROGRESS' | 'CHECKOUT_INITIATED' | 'SUBMITTED' | 'PROVISIONED';

// ---------------------------------------------------------------------------
// Request
// ---------------------------------------------------------------------------

export interface ServiceQualificationAddress {
  unitNumber?: string;
  streetNumber: string;
  streetName: string;
  city: string;
  province: string;
  postalCode: string;
  country: string;
}

export interface ServiceQualificationRequest {
  address: ServiceQualificationAddress;
}

// ---------------------------------------------------------------------------
// Access Restrictions (from AMS / Address Management System)
// ---------------------------------------------------------------------------

export type AccessRestrictionsType =
  | 'TIME_WINDOW'
  | 'BUZZER_REQUIRED'
  | 'KEY_CODE'
  | 'GATE_CODE'
  | 'SECURITY_CLEARANCE'
  | 'ESCORT_REQUIRED'
  | 'APPOINTMENT_ONLY'
  | 'NO_WEEKEND_ACCESS'
  | 'NO_HOLIDAY_ACCESS'
  | 'LOADING_DOCK_REQUIRED'
  | 'ELEVATOR_REQUIRED'
  | 'PARKING_RESTRICTION'
  | 'PET_ON_PREMISES'
  | 'HEIGHT_RESTRICTION'
  | 'WEIGHT_RESTRICTION'
  | 'HAZARDOUS_AREA'
  | 'NOISE_RESTRICTION'
  | 'CONSTRUCTION_ZONE'
  | 'TENANT_APPROVAL'
  | 'OTHER';

export type AccessRestrictionImpact = 'CUSTOMER' | 'ALL' | 'BOOKING';

export type AccessRestrictionStatus = 'ACTIVE' | 'INACTIVE';

export type AccessRestriction = {
  id: string;
  type: AccessRestrictionsType;
  description: string;
  instructions?: string;
  impact: AccessRestrictionImpact;
  status: AccessRestrictionStatus;
  validUntil: string;
  rules?: Record<string, unknown>;
};

// ---------------------------------------------------------------------------
// Response — TMF-shaped service qualification response from BE
// ---------------------------------------------------------------------------

export interface GeographicSubAddress {
  subAddressType: string;
  unitNumber: string;
}

export interface QualifiedAddress {
  id: string;
  streetNr: string;
  streetName: string;
  postcode: string;
  city: string;
  stateOrProvince: string;
  country: string;
  geographicSubAddress?: GeographicSubAddress[];
}

export type ReceivedAddress = {
  id: string;
  parentId: string;
  unitNumber?: string;
  unitType?: string;
  streetNumber: string;
  streetName: string;
  city: string;
  province: string;
  country: string;
  postCode: string;
  addressType: AddressType;
  status: QualificationStatus;
  buildingType?: string;
  onNet: boolean;
};

export interface ServiceQualificationItem {
  id: string;
  state: string;
  qualifiedAddress: QualifiedAddress;
  receivedAddress?: ReceivedAddress;
  addressQualificationStatus: QualificationStatus;
  addressType: AddressType;
}

export interface ServiceQualificationResponse {
  id: string;
  state: string;
  qualificationResult: string;
  description: string;
  serviceQualificationItem: ServiceQualificationItem;
  resolvedAddressId: string;
  resolvedAddressType: 'PARENT' | 'HOME';
  onNet: boolean;
  accessRestrictions?: AccessRestriction[];
  dropConnected?: boolean;
  technology?: string;
  createWorkOrderManually: boolean;
  isParentAddress: boolean;
  gldsHomeId?: string | null;
}

/**
 * The API returns either a full response object or an empty array `[]`
 * when no qualified address is found (route to Lead Capture in that case).
 */
export type ServiceQualificationResult = ServiceQualificationResponse | [];

// ---------------------------------------------------------------------------
// Error
// ---------------------------------------------------------------------------

/** Structured error shape returned by CPQ BE on 4xx/5xx responses */
export interface QualificationErrorResponse {
  code?: string;
  message?: string;
  requestId?: string;
  requestDatetime?: string;
  errorCode?: string;
  errorReason?: string;
  errorDescription?: string;
  reason?: string;
  /** GLDS wraps errors in a nested `{ error: { code, message } }` envelope */
  error?: { code?: string; message?: string };
}

// ---------------------------------------------------------------------------
// Routing
// ---------------------------------------------------------------------------

export enum LeadCaptureReason {
  ADDRESS_NOT_FOUND = 'ADDRESS_NOT_FOUND',
  PLANNED = 'PLANNED',
  CONSTRUCTION = 'CONSTRUCTION',
  BUSINESS = 'BUSINESS',
  OFF_NET = 'OFF_NET',
  SYSTEM_ERROR = 'SYSTEM_ERROR',
  NON_BOOKABLE = 'NON_BOOKABLE',
}

export interface LeadCaptureContext {
  defaultCustomerType: 'residential' | 'business';
  reason: LeadCaptureReason;
  prePopulatedAddress?: Omit<NormalizedAddress, 'formattedAddress'>;
}

/**
 * Full qualification store write for any qualification that produced a real
 * response. `resolvedAddressId` is the canonical id for product, equipment,
 * and customer lookups; `qualifiedAddress.id` rides inside
 * `qualificationResult` for the port-reservation gate (the two-ID rule).
 */
export interface QualificationSuccessStoreUpdate {
  resolvedAddressId: string;
  isCreateWorkOrderManually: boolean;
  qualificationResult: ServiceQualificationResponse;
  onNet: boolean;
  addressType: AddressType;
  qualificationStatus: QualificationStatus;
  leadCaptureContext: LeadCaptureContext | null;
  leadCaptureReason: LeadCaptureReason | null;
  customerType: 'RESIDENTIAL' | 'BUSINESS' | null;
}

/**
 * Three-key write for ADDRESS_NOT_FOUND (empty-array result or AMS-404):
 * every other qualification key is deliberately left untouched.
 */
export interface AddressNotFoundStoreUpdate {
  qualificationResult: null;
  leadCaptureContext: LeadCaptureContext;
  leadCaptureReason: LeadCaptureReason;
}

export type QualificationStoreUpdate = QualificationSuccessStoreUpdate | AddressNotFoundStoreUpdate;

/**
 * Everything the qualification router can route: a real response, the
 * empty-array "no qualified address" result, or the AMS-404 error outcome
 * (the axios interceptor maps the response body `code` onto `error.name`).
 */
export type QualificationRouterInput = ServiceQualificationResult | 'AMS-404';

export type QualificationRouteResult =
  | { destination: 'PRODUCT_LANDING'; resolvedAddressId: string; storeUpdate?: QualificationSuccessStoreUpdate }
  | { destination: 'LEAD_CAPTURE'; leadCaptureContext: LeadCaptureContext; storeUpdate?: QualificationStoreUpdate }
  | { destination: 'INLINE_ERROR'; storeUpdate?: undefined }
  | { destination: 'REQUEST_UNIT'; storeUpdate?: undefined };
